import { useContext, useState } from 'react';
import { Alert, Chip, Stack, Tooltip } from '@mui/material';

import GhostButton from '../../shared/button/GhostButton';
import { CaptionText } from '../../shared/typography/PrimaryTypography';
import {
  toProblemDetail,
  updateRecord
} from '../../../services/syntiro_honey/data_ingestion.service';
import { EsgRecord, ProblemDetail } from '../../../types/syntiro_honey';
import { FeedbackSnackbarContext } from '../../../context/FeedbackSnackbarContext';
import { aliasOutcomeFeedback } from './alias_feedback';
import {
  MATCH_STRENGTH_COLOR,
  MATCH_STRENGTH_LABEL,
  describeMatchStrength,
  deriveMatchStrength
} from './match_strength';
import { parseMappingReason } from './mapping_reason';

/** How many of the surfaced candidates to offer as quick-assign suggestions. */
const SUGGESTION_COUNT = 3;

interface MetricSuggestionPickerProps {
  /** The unmapped record whose suggestions are being offered. */
  record: EsgRecord;
  /** Called after a suggestion is assigned, so the parent can refresh + collapse. */
  onAssigned: () => void;
  /** Called when the reviewer wants the full search (opens the detail drawer). */
  onSearchAll: () => void;
}

/**
 * Compact inline picker for an unmapped record: the top-3 nearest metric
 * suggestions as clickable chips (each with its score), plus a "search all"
 * fallback to the detail drawer. Picking a chip ASSIGNS the metric but leaves the
 * record PENDING_REVIEW — nothing is auto-approved, and the score is shown so a
 * low-confidence suggestion reads as a hint, not a verdict.
 * @returns {JSX.Element}
 */
export default function MetricSuggestionPicker({
  record,
  onAssigned,
  onSearchAll
}: MetricSuggestionPickerProps) {
  const [saving, setSaving] = useState<boolean>(false);
  const [problem, setProblem] = useState<ProblemDetail | null>(null);

  const { setFeedbackData } = useContext(FeedbackSnackbarContext);
  const suggestions = (record.mappingCandidates ?? []).slice(0, SUGGESTION_COUNT);

  const handleAssign = async (metricId: string): Promise<void> => {
    setSaving(true);
    setProblem(null);
    try {
      const updated = await updateRecord(record.id, { metricId });
      // Assigning a suggestion is a confirmation too — surface what it taught.
      setFeedbackData(aliasOutcomeFeedback(updated));
      onAssigned();
    } catch (error: unknown) {
      setProblem(toProblemDetail(error));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Stack spacing={0.75} sx={{ py: 1 }}>
      <CaptionText weight="Medium">Suggestions — confirm before approving</CaptionText>
      <Stack spacing={0.5}>
        {suggestions.map((candidate) => {
          // Same vocabulary as the detail modal's picker: a reviewer moves between
          // the two, and one raw float here against banded words there would be two
          // descriptions of the same number. The float survives in the tooltip.
          const strength = deriveMatchStrength(candidate.score);
          const { basis, unitConflict } = parseMappingReason(candidate.reason);
          return (
            <Tooltip
              key={candidate.metricId}
              title={
                `${candidate.standardName} · ${candidate.metricGroup} — ` +
                `${describeMatchStrength(candidate.score)} ${unitConflict ?? basis}`
              }
            >
              <Chip
                label={`${candidate.name} · ${MATCH_STRENGTH_LABEL[strength]}`}
                size="small"
                variant="outlined"
                color={MATCH_STRENGTH_COLOR[strength]}
                clickable
                disabled={saving}
                onClick={() => void handleAssign(candidate.metricId)}
                sx={{ justifyContent: 'flex-start', maxWidth: '100%' }}
              />
            </Tooltip>
          );
        })}
      </Stack>
      <GhostButton
        dark
        size="small"
        disabled={saving}
        onClick={onSearchAll}
        sx={{ alignSelf: 'flex-start', minWidth: 0, px: 0 }}
      >
        Search all metrics…
      </GhostButton>
      {problem !== null && (
        <Alert severity="error">{problem.detail ?? problem.title ?? 'Assign failed.'}</Alert>
      )}
    </Stack>
  );
}
